// @ts-nocheck
/**
 * TeamLeader Workspace — Analytics Page
 * Client-side aggregation of the TeamLeader's led groups + students into
 * performance metrics: status distribution, per-group comparison, and an
 * at-risk watchlist. Uses the same data source as the rest of the workspace
 * (fetchTLGroupsWithStudents), so no dedicated backend endpoint is required.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { fetchTLGroupsWithStudents } from "@/lib/api/tl-dashboard";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  BarChart3,
  ArrowLeft,
  Users,
  Activity,
  AlertTriangle,
  TrendingUp,
  FolderOpen,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

export const Route = createFileRoute("/_authenticated/team-leader/analytics")({
  component: TeamLeaderAnalytics,
});

// ── Status helpers (shared vocabulary across the workspace) ────────────────────
const isActive = (s: string) => ["approved", "active", "Active"].includes(s);
const isAtRisk = (s: string) => ["at_risk", "AtRisk", "at-risk", "flagged", "behind"].includes(s);
const isCompleted = (s: string) => ["completed", "Completed", "Approved", "graduated"].includes(s);

function TeamLeaderAnalytics() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ["tl", "groups-with-students"],
    queryFn: fetchTLGroupsWithStudents,
    enabled: !!user?.id,
    staleTime: 30_000,
  });

  const stats = useMemo(() => {
    const total = groups.reduce((sum, g) => sum + (g.studentCount || 0), 0);
    const active = groups.reduce((sum, g) => sum + (g.activeCount || 0), 0);
    const atRisk = groups.reduce((sum, g) => sum + (g.atRiskCount || 0), 0);
    const completed = groups.reduce((sum, g) => sum + (g.completedCount || 0), 0);
    const other = Math.max(0, total - active - atRisk - completed);

    // Per-group breakdown, sorted by at-risk desc so hotspots surface first.
    const perGroup = groups
      .map((g) => {
        const n = g.studentCount || 0;
        const a = g.activeCount || 0;
        const r = g.atRiskCount || 0;
        const c = g.completedCount || 0;
        const completion = n > 0 ? Math.round(((a + c) / n) * 100) : 0;
        return { ...g, n, a, r, c, completion };
      })
      .sort((x, y) => y.r - x.r || y.n - x.n);

    const avgCompletion =
      perGroup.length > 0
        ? Math.round(perGroup.reduce((sum, g) => sum + g.completion, 0) / perGroup.length)
        : 0;

    return { total, active, atRisk, completed, other, perGroup, avgCompletion };
  }, [groups]);

  const distribution = [
    { label: "Active", value: stats.active, token: "var(--success)" },
    { label: "At Risk", value: stats.atRisk, token: "var(--warning)" },
    { label: "Completed", value: stats.completed, token: "var(--chart-3)" },
    { label: "Other", value: stats.other, token: "var(--chart-5)" },
  ];

  const kpis = [
    {
      label: "Total Students",
      value: stats.total,
      icon: Users,
      token: "var(--chart-1)",
      sub: "across all groups",
    },
    {
      label: "Active",
      value: stats.active,
      icon: Activity,
      token: "var(--success)",
      sub: "engaged learners",
    },
    {
      label: "At Risk",
      value: stats.atRisk,
      icon: AlertTriangle,
      token: "var(--warning)",
      sub: "need attention",
    },
    {
      label: "Groups",
      value: stats.perGroup.length,
      icon: FolderOpen,
      token: "var(--chart-2)",
      sub: "led by you",
    },
    {
      label: "Avg Completion",
      value: `${stats.avgCompletion}%`,
      icon: TrendingUp,
      token: "var(--accent)",
      sub: "across groups",
    },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate({ to: "/team-leader/dashboard" })}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Analytics</h1>
          <p className="text-muted-foreground text-sm">
            Performance metrics and completion trends across your led groups
          </p>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {isLoading
          ? Array.from({ length: 5 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-5">
                  <Skeleton className="h-4 w-20 mb-3" />
                  <Skeleton className="h-8 w-16 mb-2" />
                  <Skeleton className="h-3 w-24" />
                </CardContent>
              </Card>
            ))
          : kpis.map((k) => (
              <Card
                key={k.label}
                className="overflow-hidden border-t-[3px]"
                style={{ borderTopColor: k.token }}
              >
                <CardContent className="p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {k.label}
                    </span>
                    <div
                      className="h-8 w-8 rounded-lg flex items-center justify-center"
                      style={{ background: k.token }}
                    >
                      <k.icon className="h-4 w-4 text-white" />
                    </div>
                  </div>
                  <p className="text-2xl font-bold tracking-tight">{k.value}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{k.sub}</p>
                </CardContent>
              </Card>
            ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status distribution */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-primary" />
              Status Distribution
            </CardTitle>
            <CardDescription>How your students break down</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoading ? (
              <Skeleton className="h-40 w-full" />
            ) : stats.total === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">No student data yet.</p>
            ) : (
              distribution.map((d) => {
                const pct = stats.total > 0 ? Math.round((d.value / stats.total) * 100) : 0;
                return (
                  <div key={d.label} className="space-y-1.5">
                    <div className="flex justify-between text-xs font-medium">
                      <span className="flex items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ background: d.token }}
                        />
                        {d.label}
                      </span>
                      <span className="text-muted-foreground">
                        {d.value} · {pct}%
                      </span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, background: d.token }}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Per-group performance */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-primary" />
              Group Performance
            </CardTitle>
            <CardDescription>Completion and risk by group — highest-risk first</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton key={i} className="h-12 w-full" />
                ))}
              </div>
            ) : stats.perGroup.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground">
                <FolderOpen className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No groups assigned yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {stats.perGroup.map((g) => (
                  <button
                    key={g.id}
                    onClick={() =>
                      navigate({ to: "/team-leader/groups/$groupId", params: { groupId: g.id } })
                    }
                    className="w-full text-left p-3 rounded-lg border hover:bg-accent/40 transition-colors group"
                  >
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <p className="font-semibold text-sm truncate group-hover:text-primary transition-colors">
                          {g.name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {g.n} student{g.n !== 1 ? "s" : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {g.r > 0 && (
                          <Badge
                            variant="outline"
                            className="text-[10px] gap-1 text-warning border-warning/40 bg-warning-muted"
                          >
                            <AlertTriangle className="h-3 w-3" />
                            {g.r}
                          </Badge>
                        )}
                        <Badge
                          variant="outline"
                          className="text-[10px] gap-1 text-success border-success/40 bg-success-muted"
                        >
                          <CheckCircle2 className="h-3 w-3" />
                          {g.a}
                        </Badge>
                        <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Progress value={g.completion} className="h-2 flex-1" />
                      <span className="text-xs font-semibold w-10 text-right">{g.completion}%</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
